import React from 'react';
import { Row, Col, Form, Input, Select, Slider, ColorPicker, Typography, Tooltip, theme } from 'antd';
import {
    QrcodeOutlined, PrinterOutlined, CloseOutlined,
    ClockCircleOutlined, PlusOutlined, UsergroupDeleteOutlined,
    CloseCircleOutlined, InfoCircleOutlined,
    FontColorsOutlined, FontSizeOutlined, ItalicOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../context/AuthContext';
import { TEXT_FONT_OPTIONS, fontFamilyToCss } from '../helpers/textFonts';

const { Text } = Typography;

// Text size slider: the app default size is 60 (a stored 0 also means the app default)
const DEFAULT_TEXT_FONT_SIZE = 60;
const MIN_TEXT_FONT_SIZE = 8;
const MAX_TEXT_FONT_SIZE = 120;

// Slider wrapped in an input-like bordered box, so it lines up with the other fields.
// Form.Item injects value/onChange via props, they are forwarded to the inner Slider.
const BorderedSlider = (props) => {
    const { token } = theme.useToken();

    return (
        <div
            style={{
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                height: token.controlHeight,
                padding: '0 14px',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Slider {...props} style={{ flex: 1, margin: 0 }} />
        </div>
    );
};

// Render each font option in its own typeface (web fonts loaded in public/index.html)
const styledFontOptions = TEXT_FONT_OPTIONS.map((option) => ({
    value: option.value,
    label: (
        <span style={{ fontFamily: fontFamilyToCss(option.value) }}>
            {option.label}
        </span>
    ),
}));

const TabTexts = ({ showEFN }) => {
    const form = Form.useFormInstance();
    const { hasFeature, isAdmin } = useAuth?.() || { hasFeature: () => false, isAdmin: false };

    // Text style customisation (color, size, font) is a paid client feature
    const showDesign = hasFeature('deviceConfig.design') || isAdmin;

    return (
    <>
        <Row style={{ marginBottom: 30 }}>
            <Col span={24}>
                <Text type="secondary">
                    Define the custom messages shown to the user during different phases of the check-in process.
                </Text>
                <br />
                <Text type="secondary">
                    {'Texts can contain placeholders that the device fills with the data of the scanned participant: '}
                    <Text code>{'${data.name}'}</Text> <Text code>{'${data.title}'}</Text>{' '}
                    <Text code>{'${data.firstName}'}</Text> <Text code>{'${data.lastName}'}</Text>{' '}
                    <Text code>{'${data.customFieldValue(ID)}'}</Text>
                    {' (empty before a scan, e.g. on the waiting screen). '}
                    <Text code>{'<br/>'}</Text>
                    {' inserts a line break.'}
                </Text>
            </Col>
        </Row>

        <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
                <Form.Item
                    name="TextWaiting"
                    label={
                        <>
                            <QrcodeOutlined style={{ marginRight: 4 }} />
                            Waiting
                            <Tooltip title="Text displayed when the client is idle and waiting for the next QR code.">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                            </Tooltip>
                        </>
                    }
                >
                    <Input placeholder="Please provide a QR code" />
                </Form.Item>

                <Form.Item
                    name="TextPrinting"
                    label={
                        <>
                            <PrinterOutlined style={{ marginRight: 4 }} />
                            Printing
                            <Tooltip title="Text displayed while the client is transmitting the print job to the printer.">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                            </Tooltip>
                        </>
                    }
                >
                    <Input placeholder="Printing badge" />
                </Form.Item>

                <Form.Item
                    name="TextDenying"
                    label={
                        <>
                            <CloseOutlined style={{ marginRight: 4 }} />
                            Denying
                            <Tooltip title="The QR code is not valid for printing a badge. This may be due to blacklist, already printed badge, etc.">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                            </Tooltip>
                        </>
                    }
                >
                    <Input placeholder="Cannot proceed" />
                </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
                <Form.Item
                    name="TextCheckin"
                    label={
                        <>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            Checking
                            <Tooltip title="Text displayed while the client is querying the database for the scanned QR code.">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                            </Tooltip>
                        </>
                    }
                >
                    <Input placeholder="Checking QR code" />
                </Form.Item>

                <Form.Item
                    name="TextCreating"
                    label={
                        <>
                            <PlusOutlined style={{ marginRight: 4 }} />
                            Creating
                            <Tooltip title="Text shown when a lead is created automatically, during printing or lead collection.">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                            </Tooltip>
                        </>
                    }
                >
                    <Input placeholder="Lead created" />
                </Form.Item>

                <Form.Item
                    name="TextBlacklist"
                    label={
                        <>
                            <UsergroupDeleteOutlined style={{ marginRight: 4 }} />
                            Blacklist
                            <Tooltip title="Text shown when this participant type is not allowed to print here, but may be elsewhere.">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                            </Tooltip>
                        </>
                    }
                >
                    <Input placeholder="Your participant type does not allow printing here" />
                </Form.Item>

                {showEFN && (
                    <Form.Item
                        name="TextEFNError"
                        label={
                            <>
                                <CloseCircleOutlined style={{ marginRight: 4 }} />
                                EFN Error
                                <Tooltip title="Text shown when the participant is missing a required EFN.">
                                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                                </Tooltip>
                            </>
                        }
                    >
                        <Input placeholder="Bitte registrieren Sie Ihre EFN an einem unserer Counter." />
                    </Form.Item>
                )}
            </Col>
        </Row>

        {/* Text style (color, size, font) — design feature only */}
        {showDesign && (
            <>
                <Row style={{ marginTop: 30, marginBottom: 30 }}>
                    <Col span={24}>
                        <Text type="secondary">
                            Customise the style of the texts shown on the check-in screen.
                        </Text>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    {/* Left half (aligned with the left text column above): color + size */}
                    <Col xs={24} sm={12}>
                        <Row gutter={16}>
                            <Col xs={24} sm={9}>
                                <Form.Item
                                    name="TextColor"
                                    label={
                                        <>
                                            <FontColorsOutlined style={{ marginRight: 4 }} />
                                            Text Color
                                            <Tooltip title="Color of the texts shown on the check-in screen, e.g. white for dark background images.">
                                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                                            </Tooltip>
                                        </>
                                    }
                                    initialValue="#000000"
                                >
                                    <ColorPicker
                                        showText
                                        format="hex"
                                        disabledAlpha
                                        style={{ width: '100%', justifyContent: 'flex-start' }}
                                        onChange={(color) => {
                                            const rgb = color?.toRgb?.();

                                            // if transparent selected → force black
                                            if (rgb?.a === 0) {
                                                form.setFieldValue('TextColor', '#000000');
                                                return;
                                            }

                                            form.setFieldValue(
                                                'TextColor',
                                                color?.toHexString?.() || '#000000'
                                            );
                                        }}
                                        onClear={() => {
                                            form.setFieldValue('TextColor', '#000000');
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={15}>
                                <Form.Item
                                    name="TextFontSize"
                                    label={
                                        <>
                                            <FontSizeOutlined style={{ marginRight: 4 }} />
                                            Text Size
                                            <Tooltip title="Font size of the texts shown on the check-in screen. The app default size is 60.">
                                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                                            </Tooltip>
                                        </>
                                    }
                                    initialValue={DEFAULT_TEXT_FONT_SIZE}
                                    // stored 0 means "app default" — shown as the default size
                                    getValueProps={(value) => ({
                                        value: value > 0 ? value : DEFAULT_TEXT_FONT_SIZE,
                                    })}
                                >
                                    <BorderedSlider
                                        min={MIN_TEXT_FONT_SIZE}
                                        max={MAX_TEXT_FONT_SIZE}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>

                    {/* Right half (aligned with the right text column above): font */}
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="TextFontFamily"
                            label={
                                <>
                                    <ItalicOutlined style={{ marginRight: 4 }} />
                                    Text Font
                                    <Tooltip title="Font of the texts shown on the check-in screen.">
                                        <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                                    </Tooltip>
                                </>
                            }
                        >
                            <Select
                                options={styledFontOptions}
                                placeholder="Default (Open Sans)"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </>
        )}
    </>
    );
};

export default TabTexts;